import { Sparkles } from 'lucide-react'
import { useState } from 'react'
import type { CatalogCompleteness } from '~/api/types'
import { ConfirmDialog } from '~/components/ConfirmDialog'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Card } from '~/components/ui/card'
import { Spinner } from '~/components/ui/spinner'
import { cn } from '~/lib/utils'

// 카탈로그 데이터 완전성 패널 — 질의 미작성 링크·수집필드 없는 항목을 한눈에 보여 주고,
// superadmin은 빈 사전 질의를 LLM으로 일괄 생성한다(수 분 소요 가능). completeness API 기반.
// 부수효과(API 호출·토스트·새로고침)는 부모가 onGenerate로 처리한다(패널은 표시 + 확인만).
export function CompletenessPanel({
    completeness,
    canGenerate,
    generating,
    onGenerate,
}: {
    completeness: CatalogCompleteness | null
    /** superadmin일 때만 생성 버튼 노출. */
    canGenerate: boolean
    /** 생성 진행 중(버튼 비활성 + 스피너). */
    generating: boolean
    /** 확인 후 호출 — 부모가 generateMissingQuestions + 토스트 + 새로고침을 담당. */
    onGenerate: () => void
}) {
    const [confirmOpen, setConfirmOpen] = useState(false)
    if (!completeness) return null

    const { itemsWithoutFields, linksWithoutQuestion } = completeness
    const questionsComplete = linksWithoutQuestion === 0
    const showGenerate = canGenerate && linksWithoutQuestion > 0

    return (
        <Card className='mb-5'>
            <div className='flex flex-wrap items-center justify-between gap-3 px-4 py-3'>
                <div className='flex flex-wrap items-center gap-x-5 gap-y-2'>
                    <span className='krds-label'>데이터 완전성</span>
                    {questionsComplete ? (
                        <Badge tone='success'>✓ 질의 완비</Badge>
                    ) : (
                        <Metric
                            label='질의 미작성 링크'
                            value={linksWithoutQuestion}
                            tone='warning'
                        />
                    )}
                    <Metric
                        label='수집필드 없는 항목'
                        value={itemsWithoutFields}
                        tone={itemsWithoutFields > 0 ? 'warning' : undefined}
                    />
                    {itemsWithoutFields > 0 ? (
                        <span className='text-muted-foreground text-[12px]'>
                            엑셀로 수집데이터 추가 필요
                        </span>
                    ) : null}
                </div>
                {showGenerate ? (
                    <Button
                        size='sm'
                        onClick={() => setConfirmOpen(true)}
                        disabled={generating}>
                        {generating ? (
                            <>
                                <Spinner size='sm' />
                                생성 중…
                            </>
                        ) : (
                            <>
                                <Sparkles />빈 질의 {linksWithoutQuestion}건
                                생성
                            </>
                        )}
                    </Button>
                ) : null}
            </div>

            <ConfirmDialog
                open={confirmOpen}
                title='빈 질의 생성'
                description='LLM으로 빈 질의를 생성합니다. 수 분 걸릴 수 있어요.'
                confirmLabel='생성'
                onConfirm={() => {
                    setConfirmOpen(false)
                    onGenerate()
                }}
                onCancel={() => setConfirmOpen(false)}
            />
        </Card>
    )
}

// 소규모 인라인 지표(SummaryStrip 톤과 일치). tone=warning이면 주의색.
function Metric({
    label,
    value,
    tone,
}: {
    label: string
    value: number
    tone?: 'warning'
}) {
    return (
        <div className='flex items-baseline gap-1.5'>
            <span className='krds-label'>{label}</span>
            <span
                className={cn(
                    'text-base font-bold tabular-nums',
                    tone === 'warning' ? 'text-warning' : 'text-foreground'
                )}>
                {value.toLocaleString()}
            </span>
        </div>
    )
}
